import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import '@/App.css';
import '@/accessibility.css';
import Dashboard from './pages/Dashboard';
import StoryView from './pages/StoryView';
import QuizView from './pages/QuizView';
import Resources from './pages/Resources';
import Forum from './pages/Forum';
import ProfilePage from './pages/ProfilePage';
import ShareStory from './pages/ShareStory';
import UserStories from './pages/UserStories';
import RealVoices from './pages/RealVoices';
import LegalPage from './pages/LegalPage';
import BottomNav from './components/BottomNav';
import Footer from './components/Footer';
import WelcomeModal from './components/WelcomeModal';
import CookieConsent from './components/CookieConsent';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { Toaster } from './components/ui/sonner';

// Admin Portal Imports
import AdminLogin from './admin/pages/AdminLogin';
import AdminLayout from './admin/components/AdminLayout';
import ProtectedAdminRoute from './admin/components/ProtectedAdminRoute';
import AdminDashboard from './admin/pages/AdminDashboard';
import ScenarioManager from './admin/pages/ScenarioManager';
import ScenarioEditor from './admin/pages/ScenarioEditor';
import UserManagement from './admin/pages/UserManagement';
import AnalyticsDashboard from './admin/pages/AnalyticsDashboard';
import AdminSettings from './admin/pages/AdminSettings';
import AdminProfile from './admin/pages/AdminProfile';
import AdminInvitations from './admin/pages/AdminInvitations';
import ModerationDashboard from './admin/pages/ModerationDashboard';
import AcceptInvitation from './pages/AcceptInvitation';
import DatabaseSeedPage from './admin/DatabaseSeedPage';
import AnnouncementsManager from './admin/pages/AnnouncementsManager';
import FeedbackCenter from './admin/pages/FeedbackCenter';
import NotificationsManager from './admin/pages/NotificationsManager';

// Wrapper to conditionally show Footer and BottomNav
const AppLayout = ({ children, userId }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLegalPage = location.pathname.startsWith('/legal');

  return (
    <>
      {children}
      {!isAdminRoute && <BottomNav />}
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && !isLegalPage && <CookieConsent userId={userId} />}
    </>
  );
};

function App() {
  const [userId] = useState(() => {
    let id = localStorage.getItem('userId');
    if (!id) {
      id = 'user_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('userId', id);
    }
    return id;
  });

  const handleCreateAccount = () => {
    window.location.href = '/profile';
  };

  const handleContinueAnonymously = () => {
    // User chose to continue anonymously, nothing to do
    // Their progress will still be tracked via localStorage userId
  };

  return (
    <div className="App min-h-screen bg-white">
      <BrowserRouter>
        <AppLayout userId={userId}>
          <Routes>
            {/* Main App Routes */}
            <Route path="/" element={<Dashboard userId={userId} />} />
            <Route path="/story/:scenarioId" element={<StoryView userId={userId} />} />
            <Route path="/quiz/:scenarioId" element={<QuizView userId={userId} />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/forum" element={<Forum userId={userId} />} />
            <Route path="/profile" element={<ProfilePage userId={userId} />} />
            <Route path="/share-story" element={<ShareStory userId={userId} />} />
            <Route path="/user-stories" element={<UserStories userId={userId} />} />
            <Route path="/real-voices" element={<RealVoices userId={userId} />} />

            {/* Legal Routes */}
            <Route path="/legal/:docType" element={<LegalPage />} />

            {/* Admin Portal Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/admin" element={
              <ProtectedAdminRoute>
                <AdminLayout />
              </ProtectedAdminRoute>
            }>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="scenarios" element={<ScenarioManager />} />
              <Route path="scenarios/new" element={<ScenarioEditor />} />
              <Route path="scenarios/:id/edit" element={<ScenarioEditor />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              <Route path="moderation" element={
                <ProtectedAdminRoute requiredRoles={['super_admin', 'programme_manager', 'moderator']}>
                  <ModerationDashboard />
                </ProtectedAdminRoute>
              } />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="invitations" element={
                <ProtectedAdminRoute requiredRoles={['super_admin']}>
                  <AdminInvitations />
                </ProtectedAdminRoute>
              } />
              <Route path="settings" element={
                <ProtectedAdminRoute requiredRoles={['super_admin']}>
                  <AdminSettings />
                </ProtectedAdminRoute>
              } />
              <Route path="database" element={
                <ProtectedAdminRoute requiredRoles={['super_admin']}>
                  <DatabaseSeedPage />
                </ProtectedAdminRoute>
              } />
              <Route path="announcements" element={
                <ProtectedAdminRoute requiredRoles={['super_admin', 'programme_manager']}>
                  <AnnouncementsManager />
                </ProtectedAdminRoute>
              } />
              <Route path="feedback" element={
                <ProtectedAdminRoute requiredRoles={['super_admin', 'programme_manager', 'moderator']}>
                  <FeedbackCenter />
                </ProtectedAdminRoute>
              } />
              <Route path="notifications" element={
                <ProtectedAdminRoute requiredRoles={['super_admin', 'programme_manager']}>
                  <NotificationsManager />
                </ProtectedAdminRoute>
              } />
            </Route>
          </Routes>
          <WelcomeModal 
            onCreateAccount={handleCreateAccount}
            onContinueAnonymously={handleContinueAnonymously}
            userId={userId}
          />
          <PWAInstallPrompt />
          <Toaster position="top-center" />
        </AppLayout>
      </BrowserRouter>
    </div>
  );
}

export default App;
